import * as THREE from 'three';
import { mergeGeometries as mergeBufferGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * Merges multiple buffer geometries into one (task 3.2).
 *
 * Resolves the former "return first for now" TODOs in StairSolver and
 * RebarSolver by using three's BufferGeometryUtils.mergeGeometries, which
 * preserves UVs/normals (recomputed when missing) instead of dropping them.
 * Returns null when nothing can be merged.
 *
 * Robustness notes:
 * - mergeGeometries() requires every geometry to share the same attribute set
 *   and the same indexed/non-indexed layout, so inputs are normalized first:
 *   attributes not present on ALL geometries are dropped, and all geometries
 *   are converted to non-indexed whenever any input lacks an index.
 */
export function mergeGeometries(
    geometries: THREE.BufferGeometry[]
): THREE.BufferGeometry | null {
    if (geometries.length === 0) return null;

    const anyMissingIndex = geometries.some((geo) => !geo.getIndex());

    // Determine the attribute set shared by every geometry
    const attributeNames = geometries.map((geo) => Object.keys(geo.attributes));
    let sharedAttributes = attributeNames[0] ?? [];
    for (let i = 1; i < attributeNames.length; i++) {
        sharedAttributes = sharedAttributes.filter((name) => attributeNames[i].includes(name));
    }

    const normalized = geometries.map((geo) => {
        let prepared = geo;
        if (anyMissingIndex && geo.getIndex()) {
            prepared = geo.toNonIndexed();
        }
        // Delete attributes not shared by all geometries
        Object.keys(prepared.attributes).forEach((name) => {
            if (!sharedAttributes.includes(name)) {
                prepared.deleteAttribute(name);
            }
        });
        // Guarantee normals for shading
        if (!prepared.getAttribute('normal')) {
            prepared.computeVertexNormals();
        }
        return prepared;
    });

    const merged = mergeBufferGeometries(normalized, false);
    if (merged) {
        merged.computeBoundingSphere();
        merged.computeBoundingBox();
    }
    return merged;
}

/**
 * Merges geometries and wraps the result in a Mesh.
 * Falls back to the first geometry when merging is not possible.
 */
export function mergeGeometriesIntoMesh(
    geometries: THREE.BufferGeometry[],
    material?: THREE.Material
): THREE.Mesh {
    const merged = mergeGeometries(geometries);
    if (!merged) return new THREE.Mesh();
    return new THREE.Mesh(merged, material);
}

